if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.navButtons = this.querySelectorAll('.product-media-modal__nav');
        if (this.navButtons.length) {
          this.prevButton = this.querySelector('.product-media-modal__nav--prev');
          this.nextButton = this.querySelector('.product-media-modal__nav--next');
          this.counter = this.querySelector('.product-media-modal__counter');
          this.counterCurrent = this.querySelector('.product-media-modal__counter-current');
          this.counterTotal = this.querySelector('.product-media-modal__counter-total');

          this.navButtons.forEach((button) => {
            button.addEventListener('click', this.onNavClick.bind(this));
            // The media-modal backdrop handler hides the modal on mouse pointerup.
            // Stop the event from bubbling so using the arrows keeps the lightbox open.
            button.addEventListener('pointerup', (event) => event.stopPropagation());
          });
        }

        this.initMagnify();
        this.initThumbnails();
      }

      initThumbnails() {
        this.thumbnailButtons = Array.from(
          this.querySelectorAll('.product-media-modal__thumbnails .product-media-modal__thumbnail')
        );
        if (!this.thumbnailButtons.length) return;

        this.thumbnailButtons.forEach((button) => {
          button.addEventListener('click', () => {
            const mediaId = button.getAttribute('data-media-id');
            const target = this.getMediaItems().find((item) => item.dataset.mediaId === mediaId);
            if (target) this.activateMedia(target);
          });
          // Keep the lightbox open when the click ends over the thumbnail rail.
          button.addEventListener('pointerup', (event) => event.stopPropagation());
        });
      }

      updateThumbnailState(activeId) {
        if (!this.thumbnailButtons || !this.thumbnailButtons.length) return;
        this.thumbnailButtons.forEach((button) => {
          const isActive = button.getAttribute('data-media-id') === activeId;
          button.classList.toggle('is-active', isActive);
          if (isActive) button.scrollIntoView({ block: 'nearest' });
        });
      }

      initMagnify() {
        // Click/tap a photo in the fullscreen viewer to magnify the area under
        // the pointer, then move the mouse (or drag a finger) to pan across the
        // close-up. Click/tap the photo again to zoom back out. The side arrows
        // slide to the next photo and keep the close-up active.
        this.magnifyOverlay = null;
        this.magnifyImage = null;
        this.magnifyActive = false;
        this.magnifyPointer = { x: 0, y: 0 };
        this.pressStartedOnPhoto = false;
        this.pressPointerId = null;
        this.pressMoved = false;

        const content = this.querySelector('.product-media-modal__content');
        if (!content) return;

        content.addEventListener('pointerdown', this.onMagnifyPointerDown.bind(this));
        content.addEventListener('pointermove', this.onMagnifyPointerMove.bind(this));
        content.addEventListener('pointerup', this.onMagnifyPointerUp.bind(this));
        content.addEventListener('pointercancel', this.onMagnifyPointerCancel.bind(this));
        content.addEventListener('click', this.onMagnifyClick.bind(this));
      }

      hide() {
        super.hide();
        this.removeMagnify();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      getMagnifyContent() {
        return this.querySelector('.product-media-modal__content');
      }

      // Whether the press started on a photo (or on its magnified overlay), in
      // which case releasing the pointer must keep the lightbox open.
      isMagnifyTarget(event) {
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return false;
        const content = this.getMagnifyContent();
        if (!content || !content.contains(target)) return false;
        return Boolean(target.closest('img[data-media-id], .js-product-modal-magnify'));
      }

      magnify(image, clientX, clientY) {
        this.removeMagnify();
        const content = this.getMagnifyContent();
        if (!content) return;
        if (!image.offsetWidth || !image.offsetHeight) return;

        // Reuse the highest-resolution source already used for the displayed
        // photo, so the close-up stays sharp and loads instantly from cache.
        const source = image.currentSrc || image.src;
        const naturalWidth = image.naturalWidth || image.width || image.offsetWidth;
        const naturalHeight = image.naturalHeight || image.height || image.offsetHeight;

        const overlay = document.createElement('div');
        overlay.className = 'js-product-modal-magnify';
        overlay.setAttribute('aria-hidden', 'true');
        // The content is position: relative, so the overlay (absolute) lines up
        // exactly with the photo's layout box.
        overlay.style.left = `${image.offsetLeft}px`;
        overlay.style.top = `${image.offsetTop}px`;
        overlay.style.width = `${image.offsetWidth}px`;
        overlay.style.height = `${image.offsetHeight}px`;
        overlay.style.backgroundImage = `url('${source}')`;
        overlay.style.backgroundRepeat = 'no-repeat';
        overlay.style.backgroundColor = 'rgb(0, 0, 0)';
        // Show 2x the source pixels inside the frame: a crisp close-up that
        // pans with the pointer (same technique as the theme's hover zoom).
        overlay.style.backgroundSize = `${naturalWidth * 2}px`;
        content.appendChild(overlay);

        this.magnifyOverlay = overlay;
        this.magnifyImage = image;
        this.magnifyActive = true;
        this.moveMagnify(clientX, clientY);
      }

      moveMagnify(clientX, clientY) {
        this.magnifyPointer = { x: clientX, y: clientY };
        if (!this.magnifyOverlay || !this.magnifyImage) return;
        const overlay = this.magnifyOverlay;
        const rect = overlay.getBoundingClientRect();
        if (!rect.width || !rect.height) return;

        const image = this.magnifyImage;
        const ratio =
          (image.naturalHeight || image.offsetHeight) / (image.naturalWidth || image.offsetWidth);
        const xPercent = ((clientX - rect.left) / (rect.width / 100)).toFixed(2);
        const yPercent = ((clientY - rect.top) / ((rect.width * ratio) / 100)).toFixed(2);

        overlay.style.backgroundPosition = `${Math.min(Math.max(xPercent, 0), 100)}% ${Math.min(
          Math.max(yPercent, 0),
          100
        )}%`;
      }

      removeMagnify() {
        this.magnifyOverlay?.remove();
        this.magnifyOverlay = null;
        this.magnifyImage = null;
        this.magnifyActive = false;
        this.pressPointerId = null;
        this.pressStartedOnPhoto = false;
        this.pressMoved = false;
      }

      onMagnifyPointerDown(event) {
        if (!this.isMagnifyTarget(event)) return;

        this.pressStartedOnPhoto = true;
        this.pressPointerId = event.pointerId;
        this.pressMoved = false;
      }

      onMagnifyPointerMove(event) {
        // Mouse hover and touch drag both pan the close-up to the pointer.
        if (this.magnifyActive) this.moveMagnify(event.clientX, event.clientY);

        if (this.pressStartedOnPhoto && event.pointerId === this.pressPointerId) {
          const distance = Math.abs(event.clientX - (this.pressX ?? event.clientX)) + Math.abs(
            event.clientY - (this.pressY ?? event.clientY)
          );
          if (distance > 8) this.pressMoved = true;
          this.pressX = event.clientX;
          this.pressY = event.clientY;
        }
      }

      onMagnifyPointerUp(event) {
        if (!this.pressStartedOnPhoto) return;
        if (event.pointerId !== this.pressPointerId) return;

        // A press that started on a photo never closes the lightbox, even when
        // it ends over the backdrop after a drag.
        event.stopPropagation();
        this.pressStartedOnPhoto = false;
        this.pressPointerId = null;
        this.pressX = undefined;
        this.pressY = undefined;
      }

      onMagnifyPointerCancel(event) {
        if (event.pointerId === this.pressPointerId) {
          this.pressStartedOnPhoto = false;
          this.pressPointerId = null;
          this.pressX = undefined;
          this.pressY = undefined;
        }
      }

      onMagnifyClick(event) {
        const target = event.target instanceof Element ? event.target : null;
        if (!target || this.pressMoved) return;

        // Clicking/tapping the magnified photo zooms back out.
        if (this.magnifyActive && target.closest('.js-product-modal-magnify')) {
          this.removeMagnify();
          return;
        }

        // Clicking/tapping a photo opens the close-up.
        const image = target.closest('img[data-media-id]');
        const content = this.getMagnifyContent();
        if (image && content && content.contains(image)) {
          this.magnify(image, event.clientX, event.clientY);
        }
      }

      showActiveMedia() {
        this.removeMagnify();
        // Media lookups MUST be scoped to the content area. The thumbnail rail
        // buttons also carry data-media-id and sit EARLIER in the DOM, so an
        // unscoped query matched a thumbnail first: the real photo never got
        // the .active class, and since the modal CSS hides every item without
        // it, the lightbox opened empty with a "0 / N" counter.
        const content = this.querySelector('.product-media-modal__content');
        if (!content) return;
        const mediaId = this.openedBy ? this.openedBy.getAttribute('data-media-id') : null;
        content.querySelectorAll(`[data-media-id]:not([data-media-id="${mediaId}"])`).forEach(
          (element) => {
            element.classList.remove('active');
          }
        );
        const activeMedia =
          (mediaId ? content.querySelector(`[data-media-id="${mediaId}"]`) : null) ||
          content.querySelector('[data-media-id]');
        if (!activeMedia) return;
        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        activeMedia.classList.add('active');
        activeMedia.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        if (container) container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();

        this.updateNavState();
        this.updateThumbnailState(activeMedia.getAttribute('data-media-id'));
      }

      onNavClick(event) {
        const direction = event.currentTarget.name === 'next' ? 1 : -1;
        const items = this.getMediaItems();
        const currentIndex = items.findIndex((item) => item.classList.contains('active'));
        if (currentIndex === -1) return;
        const target = items[currentIndex + direction];
        if (!target) return;
        this.activateMedia(target);
      }

      activateMedia(target) {
        // Remember the close-up so sliding to the next photo keeps the zoom.
        const restoreZoom =
          this.magnifyActive && target.matches('img[data-media-id]')
            ? { x: this.magnifyPointer.x, y: this.magnifyPointer.y }
            : null;

        this.removeMagnify();
        const items = this.getMediaItems();
        items.forEach((item) => item.classList.remove('active'));
        target.classList.add('active');
        target.scrollIntoView();

        const container = this.querySelector('[role="document"]');
        container.scrollLeft = (target.width - container.clientWidth) / 2;

        this.updateNavState();
        this.updateThumbnailState(target.getAttribute('data-media-id'));

        // Re-open the close-up on the newly shown photo so customers can keep
        // sliding from one magnified view to the next.
        if (restoreZoom) this.magnify(target, restoreZoom.x, restoreZoom.y);
      }

      getMediaItems() {
        return Array.from(this.querySelectorAll('.product-media-modal__content [data-media-id]'));
      }

      updateNavState() {
        if (!this.navButtons.length) return;

        const items = this.getMediaItems();
        const currentIndex = items.findIndex((item) => item.classList.contains('active'));
        const total = items.length;
        const single = total <= 1;

        this.navButtons.forEach((button) => button.classList.toggle('hidden', single));
        this.counter.classList.toggle('hidden', single);
        if (single) return;

        this.prevButton.disabled = currentIndex <= 0;
        this.nextButton.disabled = currentIndex >= total - 1;
        this.counterCurrent.textContent = currentIndex + 1;
        this.counterTotal.textContent = total;
      }
    }
  );
}
